import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  CardActionArea,
  CardContent,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import GlassCard from '../../components/Cards/GlassCard';
import InstitutionPageHeader from '../../components/InstitutionPageHeader';
import { useBranding } from '../../context/BrandingContext';
import useBatchStore from '../../store/batchStore';
import BatchInsightsCard from '../../components/CodingTracker/BatchInsightsCard';
import RecentActivityCard from '../../components/CodingTracker/RecentActivityCard';
import HubRoundedIcon from '@mui/icons-material/HubRounded';

const CodingOverview = () => {
  const { displayShortName } = useBranding();
  const { batches, fetchBatches } = useBatchStore();
  const [insightsBatchId, setInsightsBatchId] = useState('');

  useEffect(() => {
    fetchBatches(false);
  }, [fetchBatches]);

  useEffect(() => {
    if (!batches.length) {
      setInsightsBatchId('');
      return;
    }
    setInsightsBatchId((prev) => {
      if (prev && batches.some((b) => b._id === prev)) return prev;
      return batches[0]._id;
    });
  }, [batches]);

  return (
    <Box sx={{ px: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto', pb: 4 }}>
      <InstitutionPageHeader />
      <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5, letterSpacing: '-0.02em' }}>
        {displayShortName} · Coding workspace
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4, maxWidth: 720 }}>
        A quick orientation for CBC faculty using the coding tracker. Detailed work still happens under
        Coding Workspace in the sidebar.
      </Typography>

      <Grid container spacing={4}>
        <Grid item xs={12} md={4}>
          <GlassCard sx={{ height: '100%', borderRadius: 2 }}>
            <CardActionArea
              component={RouterLink}
              to="/faculty/coding-dashboard"
              sx={{
                alignItems: 'stretch',
                height: '100%',
                justifyContent: 'flex-start',
                '&:hover .MuiCardContent-root': { bgcolor: 'action.hover' },
              }}
            >
              <CardContent sx={{ p: 2.5, width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 1.5,
                      bgcolor: 'action.hover',
                      color: 'primary.main',
                      display: 'flex',
                    }}
                  >
                    <HubRoundedIcon fontSize="small" />
                  </Box>
                  <Typography variant="h6" component="h2" fontWeight={600}>
                    What the Coding Tracker does
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                  Organize students into batches, attach assignments, and pull in LeetCode-style progress
                  so you can see who is practicing and where they stall—without manual spreadsheets.
                </Typography>
              </CardContent>
            </CardActionArea>
          </GlassCard>
        </Grid>

        <Grid item xs={12} md={4} sx={{ height: '100%', mb: 4 }}>
          <GlassCard hoverable={false} sx={{ height: 'auto', borderRadius: 2 }}>
            {batches.length > 0 && insightsBatchId && (
              <CardContent sx={{ p: 2.5, width: '100%' }}>
                <FormControl size="small" sx={{ minWidth: 260, maxWidth: '100%', mb: 2 }}>
                  <InputLabel id="overview-batch-insights-label">Insights for batch</InputLabel>
                  <Select
                    labelId="overview-batch-insights-label"
                    label="Insights for batch"
                    value={insightsBatchId}
                    onChange={(e) => setInsightsBatchId(e.target.value)}
                  >
                    {batches.map((b) => (
                      <MenuItem key={b._id} value={b._id}>
                        {b.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <BatchInsightsCard batchId={insightsBatchId} />
              </CardContent>
            )}
          </GlassCard>
          <RecentActivityCard autoRefreshMs={30_000} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default CodingOverview;
